/**
 * Arrow controls for horizontally scrolling blocks in the PDP marketing column.
 *
 * The marketing column is narrower when the sticky buy block experiment is on,
 * which pushes several blocks into horizontal overflow — spec tables (authored
 * as HTML in product metafields, so they have no markup we control) and the CTA
 * card row. Native scrollbars there read as broken.
 *
 * This finds any horizontally scrollable element inside .pdp-marketing-content,
 * hides its scrollbar and gives it prev/next buttons. Detection is by computed
 * overflow rather than class name, because the metafield HTML is merchant
 * authored and its classes can change.
 *
 * Opt an element out with data-no-arrows.
 */

const ROOT = 'main .pdp-marketing-content';

class ScrollArrows {
  /** @type {WeakSet<Element>} */
  #enhanced = new WeakSet();
  #frame = 0;

  constructor() {
    const scan = () => this.schedule();

    window.addEventListener('load', scan);
    window.addEventListener('resize', scan, { passive: true });
    document.addEventListener('variant:update', scan);
    document.addEventListener('shopify:section:load', scan);
    document.fonts?.ready.then(scan);

    // Marketing blocks can hydrate late (videos, app embeds), which is what
    // turns a block into an overflowing one.
    const root = document.querySelector(ROOT);
    if (root) new MutationObserver(scan).observe(root, { childList: true, subtree: true });

    this.scan();
  }

  schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.scan();
    });
  }

  scan() {
    const root = document.querySelector(ROOT);
    if (!root) return;

    for (const el of root.querySelectorAll('*')) {
      if (this.#enhanced.has(el)) continue;
      if (el.hasAttribute('data-no-arrows')) continue;
      if (el.classList.contains('spdp-scroller')) continue;

      const overflowX = getComputedStyle(el).overflowX;
      if (overflowX !== 'auto' && overflowX !== 'scroll') continue;

      this.enhance(el);
    }
  }

  /** @param {HTMLElement} viewport */
  enhance(viewport) {
    this.#enhanced.add(viewport);

    const wrapper = document.createElement('div');
    wrapper.className = 'spdp-scroller';
    viewport.parentNode?.insertBefore(wrapper, viewport);
    wrapper.appendChild(viewport);
    viewport.classList.add('spdp-scroller__viewport');

    const prev = this.#button('prev', 'Scroll left');
    const next = this.#button('next', 'Scroll right');
    wrapper.append(prev, next);

    const update = () => {
      const max = viewport.scrollWidth - viewport.clientWidth;
      wrapper.classList.toggle('spdp-scroller--active', max > 2);
      prev.disabled = viewport.scrollLeft <= 1;
      next.disabled = viewport.scrollLeft >= max - 1;
    };

    const page = () => Math.max(200, Math.round(viewport.clientWidth * 0.85));
    prev.addEventListener('click', () => viewport.scrollBy({ left: -page(), behavior: 'smooth' }));
    next.addEventListener('click', () => viewport.scrollBy({ left: page(), behavior: 'smooth' }));

    viewport.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(viewport);
    update();
  }

  /**
   * @param {'prev' | 'next'} direction
   * @param {string} label
   */
  #button(direction, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `spdp-scroller__arrow spdp-scroller__arrow--${direction}`;
    button.setAttribute('aria-label', label);
    button.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return button;
  }
}

new ScrollArrows();
